import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ApiError, errorMessage } from '../api/client';
import {
  SOURCE_LABELS,
  catalogApi,
  isStandInIsbn,
  looksLikeIsbn,
  type ApiBook,
  type BindingType,
  type CreateBookPayload,
  type IsbnLookupResult,
  type NamedRef,
  type UpdateBookPayload,
} from '../api/catalog';
import { money } from '../format';
import { posApi, repricedMrpOf, stockLabelOf, type PosBook } from '../api/pos';
import { useAuth } from '../auth/AuthContext';
import RefPicker from '../components/RefPicker';

/** The stand-in author a D-copy or counter-only item gets by default. */
const DEFAULT_COUNTER_AUTHOR = 'Unknown Author';

/** How long typing must pause before a title is searched. */
const TITLE_SEARCH_DEBOUNCE_MS = 250;

const LANGUAGES = [
  'English',
  'Hindi',
  'Tamil',
  'Bengali',
  'Marathi',
  'Kannada',
  'Telugu',
  'Malayalam',
  'Gujarati',
  'Urdu',
];

const BINDINGS: { value: BindingType; label: string }[] = [
  { value: 'PAPERBACK', label: 'Paperback' },
  { value: 'HARDCOVER', label: 'Hardcover' },
  { value: 'SPIRAL', label: 'Spiral' },
];

const hasMalayalam = (text: string) => /[ഀ-ൿ]/.test(text);

/** What a scan filled in, kept so the form can say where its values came from. */
interface ScanSummary {
  isbn13: string;
  sourceLabel: string;
  /** True when the lookup found nothing and only the ISBN was carried over. */
  bare: boolean;
}

/**
 * A name the scan found that we have no record of yet. Held aside rather than
 * created automatically: an author or publisher record is permanent shared
 * data, and a spelling quirk in somebody else's database should not be able to
 * write itself into ours without a person agreeing to it.
 */
interface PendingRef {
  kind: 'author' | 'publisher' | 'category';
  name: string;
}

const PENDING_LABELS: Record<PendingRef['kind'], string> = {
  author: 'Author',
  publisher: 'Publisher',
  category: 'Category',
};

interface BookForm {
  title: string;
  titleMl: string;
  isbn13: string;
  isbn10: string;
  /** No ISBN: the server assigns the next BLSM-A-00001, … on save. */
  noIsbn: boolean;
  description: string;
  // Money and counts are held as typed and turned into numbers once, below.
  mrp: string;
  price: string;
  stock: string;
  isUsed: boolean;
  isOnlineVisible: boolean;
  isDCopy: boolean;
  isBestSeller: boolean;
  authorIds: string[];
  categoryIds: string[];
  publisherId: string;
  bindingType: BindingType;
  pages: string;
  language: string;
  publishedDate: string;
  /** Already on our own storage — the lookup re-hosts whatever it finds. */
  coverImageUrl: string;
  scan: ScanSummary | null;
  pendingRefs: PendingRef[];
}

const EMPTY_FORM: BookForm = {
  title: '',
  titleMl: '',
  isbn13: '',
  isbn10: '',
  noIsbn: false,
  description: '',
  mrp: '',
  price: '',
  stock: '',
  isUsed: false,
  // A new book is sold online unless "Sell online" is unticked. An edit keeps
  // whatever the book already had — see formFromBook.
  isOnlineVisible: true,
  isDCopy: false,
  isBestSeller: false,
  authorIds: [],
  categoryIds: [],
  publisherId: '',
  bindingType: 'PAPERBACK',
  pages: '',
  language: '',
  publishedDate: '',
  coverImageUrl: '',
  scan: null,
  pendingRefs: [],
};

/** Money entry: digits with at most one decimal point and two decimals. */
function sanitizeMoney(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  if (!rest.length) return whole;
  return `${whole}.${rest.join('').slice(0, 2)}`;
}

function toNumber(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Add options to a list, skipping any that are already there. */
function withOptions(prev: NamedRef[], add: NamedRef[]): NamedRef[] {
  const fresh = add.filter((a) => !prev.some((p) => p.id === a.id));
  return fresh.length ? [...prev, ...fresh] : prev;
}

/** The book an edit is changing, and the form as it stood when it was loaded. */
interface EditTarget {
  id: string;
  sku: string | null;
  stockQuantity: number;
  /** A keychain, bookmark or toy is a catalogue row too, with no author to require. */
  isBook: boolean;
  /** The price on screen is the sale's, not the book's own, so it is locked. */
  inFlashSale: boolean;
  /** The diff base: only what differs from this is sent. */
  baseline: BookForm;
}

function formFromBook(book: ApiBook): BookForm {
  return {
    ...EMPTY_FORM,
    title: book.title ?? '',
    titleMl: book.titleMl ?? '',
    isbn13: book.isbn13 ?? '',
    isbn10: book.isbn10 ?? '',
    description: book.description ?? '',
    mrp: String(book.mrp ?? ''),
    price: String(book.price ?? ''),
    stock: String(book.stockQuantity ?? 0),
    isUsed: Boolean(book.isUsed),
    isOnlineVisible: book.isOnlineVisible !== false,
    isDCopy: Boolean(book.isDCopy),
    isBestSeller: Boolean(book.isBestSeller),
    authorIds: (book.authors ?? []).map((a) => a.author.id),
    categoryIds: (book.categories ?? []).map((c) => c.category.id),
    publisherId: book.publisherId ?? '',
    bindingType: book.bindingType ?? 'PAPERBACK',
    pages: book.pages != null ? String(book.pages) : '',
    language: book.language ?? '',
    publishedDate: book.publishedDate ? book.publishedDate.slice(0, 10) : '',
    coverImageUrl: book.coverImageUrl ?? '',
  };
}

/**
 * The book a copy was started from: a used copy of it, or the same book at
 * the publisher's new MRP. `groupId` is what a used copy is saved with;
 * `linkSource` says the source was in no group yet and must be given the same
 * id afterwards, or neither would list the other. A price copy leaves both to
 * the server, which groups it with every copy of the ISBN.
 */
interface CopySource {
  kind: 'used' | 'price';
  id: string;
  title: string;
  /** The source's MRP — a price copy has to differ from it. */
  mrp: number;
  groupId: string;
  linkSource: boolean;
}

/**
 * A readable group id: the title, plus enough randomness that two unrelated
 * books called "Selected Poems" do not merge. Same shape the admin panel's
 * BookGroupPicker mints.
 */
function mintGroupId(title: string): string {
  const stem =
    title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'book';
  return `${stem}-${Math.random().toString(36).slice(2, 8)}`;
}

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

/**
 * Only what actually changed, for PATCH /catalog/books/:id — sending an
 * untouched field would rewrite it. Images are never in here: the till does
 * not change them.
 */
function changesOf(form: BookForm, target: EditTarget): UpdateBookPayload {
  const b = target.baseline;
  const p: UpdateBookPayload = {};
  const differs = (now: string, was: string) => now.trim() !== was.trim();

  if (differs(form.title, b.title)) p.title = form.title.trim();
  if (differs(form.titleMl, b.titleMl)) p.titleMl = form.titleMl.trim();
  if (form.noIsbn) p.noIsbn = true;
  else {
    if (differs(form.isbn10, b.isbn10)) p.isbn10 = form.isbn10.trim();
    if (differs(form.isbn13, b.isbn13)) p.isbn13 = form.isbn13.trim();
  }
  if (differs(form.description, b.description)) p.description = form.description.trim();
  if (!target.inFlashSale) {
    if (toNumber(form.price) !== toNumber(b.price)) p.price = toNumber(form.price);
    if (toNumber(form.mrp) !== toNumber(b.mrp)) p.mrp = toNumber(form.mrp);
  }
  // Language, pages and the date can be changed but not cleared from here —
  // the server has no empty form for any of them.
  if (form.language !== b.language && form.language) p.language = form.language;
  if (Number(form.pages) !== Number(b.pages) && Number(form.pages) > 0) {
    p.pages = Number(form.pages);
  }
  if (form.publisherId !== b.publisherId) p.publisherId = form.publisherId || null;
  if (form.publishedDate !== b.publishedDate && form.publishedDate) {
    p.publishedDate = form.publishedDate;
  }
  if (form.bindingType !== b.bindingType) p.bindingType = form.bindingType;
  if (form.isUsed !== b.isUsed) p.isUsed = form.isUsed;
  // Sent as a pair whenever either moved: the server rejects a payload asking
  // for D-copy and online together, which a half-sent pair would be while the
  // stored row is still online.
  const online = form.isDCopy ? false : form.isOnlineVisible;
  const wasOnline = b.isDCopy ? false : b.isOnlineVisible;
  if (form.isDCopy !== b.isDCopy || online !== wasOnline) {
    p.isDCopy = form.isDCopy;
    p.isOnlineVisible = online;
  }
  if (form.isBestSeller !== b.isBestSeller) p.isBestSeller = form.isBestSeller;
  if (!sameIds(form.categoryIds, b.categoryIds)) p.categoryIds = form.categoryIds;
  if (!sameIds(form.authorIds, b.authorIds)) p.authorIds = form.authorIds;
  // The figure the shelf should read afterwards. A box left empty is not a
  // count of nought — it is no answer, and nothing is sent for it.
  const stock = form.stock.trim();
  if (/^\d+$/.test(stock) && Number(stock) !== Number(b.stock)) {
    p.stockQuantity = Number(stock);
  }
  return p;
}

/** Payload key → the words staff use for it in the confirmation. */
const FIELD_LABELS: Record<string, string> = {
  title: 'title',
  titleMl: 'Malayalam title',
  isbn10: 'ISBN-10',
  isbn13: 'ISBN-13',
  noIsbn: 'no-ISBN label',
  description: 'description',
  price: 'selling price',
  mrp: 'MRP',
  language: 'language',
  pages: 'pages',
  publisherId: 'publisher',
  publishedDate: 'published date',
  bindingType: 'binding',
  isUsed: 'used flag',
  isDCopy: 'D-copy',
  isOnlineVisible: 'sell online',
  isBestSeller: 'best seller',
  categoryIds: 'categories',
  authorIds: 'authors',
  stockQuantity: 'stock',
};

const sourceLine = (result: IsbnLookupResult) =>
  result.sources.map((s) => SOURCE_LABELS[s]).join(' + ');

const downLine = (result: IsbnLookupResult) =>
  result.unavailable.map((s) => SOURCE_LABELS[s]).join(' and ');

/**
 * Add a book to the catalogue from the till, or correct one that is there.
 *
 * For the book that reaches the counter before anybody has catalogued it. The
 * same scan the Facility app does with a camera is done here with the till's
 * own barcode scanner: scan the back cover, the server looks the ISBN up and
 * the form fills itself, staff type the price printed on the book and how
 * many copies there are, and it can be billed straight away.
 *
 * The same form edits an existing book — reached from a scan of something
 * already catalogued, or from a row on Shop stock. An edit sends only what
 * changed. Stock is one of the things it can change: the figure typed is what
 * the book will have afterwards, and the confirmation reads the old and the
 * new count back before anything is saved.
 */
export default function AddBookScreen({
  active,
  editRequest,
}: {
  active: boolean;
  /** A book to open for editing. `n` changes on every ask, so the same book can be asked for twice. */
  editRequest?: { id: string; n: number } | null;
}) {
  const [form, setForm] = useState<BookForm>(EMPTY_FORM);
  const set = useCallback(
    (patch: Partial<BookForm>) => setForm((prev) => ({ ...prev, ...patch })),
    [],
  );
  // Highlight the missing fields only once a save has been attempted, so a
  // blank form does not open covered in red.
  const [showErrors, setShowErrors] = useState(false);

  // ── picker lists ──
  // Categories are few enough to load whole. Authors and publishers run to
  // thousands and are searched on the server as they are typed, so these two
  // hold only the names this form has met — what a scan matched, what was
  // picked, what was created — which is all the chips need.
  const [authors, setAuthors] = useState<NamedRef[]>([]);
  const [categories, setCategories] = useState<NamedRef[]>([]);
  const [publishers, setPublishers] = useState<NamedRef[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // ── the scan ──
  const [scanText, setScanText] = useState('');
  const [scanHint, setScanHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState<IsbnLookupResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [creatingRef, setCreatingRef] = useState<string | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  // A title typed into the scan box, searched as it is typed — the way to
  // find a book to edit when it has no barcode or the barcode is not to hand.
  const [titleResults, setTitleResults] = useState<PosBook[]>([]);
  const [titleSearching, setTitleSearching] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);

  // ── the save ──
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ heading: string; text: string } | null>(null);

  // ── the cover photo ──
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  async function uploadCover(file: File) {
    setUploadingCover(true);
    setCoverError(null);
    try {
      const uploaded = await catalogApi.uploadCover(file);
      set({ coverImageUrl: uploaded.url });
    } catch (e) {
      setCoverError(errorMessage(e, 'The image could not be uploaded.'));
    } finally {
      setUploadingCover(false);
    }
  }

  // ── the edit ──
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  // Mirrors `editing` for applyLookup, which runs after an await and would
  // otherwise read the value from the render the scan started in.
  const editingRef = useRef(false);
  // Set while the form is a used copy of a catalogued book, not a fresh one.
  const [copyOf, setCopyOf] = useState<CopySource | null>(null);

  const scanRef = useRef<HTMLInputElement>(null);
  const mrpRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  // Guards against a slow lookup landing after a newer scan.
  const lookupSeq = useRef(0);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const cats = await catalogApi.listCategories();
      // Merged rather than replaced: a scan may already have put a name here.
      setCategories((prev) => withOptions(cats, prev));
    } catch (err) {
      setOptionsError(errorMessage(err, 'Could not load the categories.'));
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  // The scanner types into whatever holds focus, so the scan box takes it
  // each time this tab is opened.
  useEffect(() => {
    if (active) scanRef.current?.focus();
  }, [active]);

  /**
   * Fill the form from a looked-up ISBN.
   *
   * Every field the lookup has a value for is written; what it does not know
   * is left as it was, so a scan never blanks out something already typed.
   * The exception is a form that an EARLIER scan filled: that is a different
   * book, and carrying its authors over onto this one is how two books get
   * mixed into one record — so it starts clean.
   */
  const applyLookup = useCallback((found: IsbnLookupResult) => {
    const book = found.book;
    // A scan that fills the form is a new book. If one was open for editing,
    // it is put down — its details must not bleed into this one.
    const wasEditing = editingRef.current;
    editingRef.current = false;
    setEditing(null);
    setCopyOf(null);

    if (book) {
      const matched = (refs: Array<{ id: string | null; name: string } | null>) =>
        refs.flatMap((r) => (r?.id ? [{ id: r.id, name: r.name }] : []));
      // So a matched name shows as itself even if the lists have not loaded.
      setAuthors((prev) => withOptions(prev, matched(book.authors)));
      setCategories((prev) => withOptions(prev, matched([book.category])));
      setPublishers((prev) => withOptions(prev, matched([book.publisher])));
    }

    setForm((prev) => {
      const base = prev.scan || wasEditing ? EMPTY_FORM : prev;
      const next: BookForm = {
        ...base,
        isbn13: found.isbn13,
        isbn10: found.isbn10 ?? base.isbn10,
        // A scanned ISBN is the book's ISBN; a stand-in would throw it away.
        noIsbn: false,
      };

      if (!book) {
        // Nothing found: the ISBN alone is still worth carrying over.
        return {
          ...next,
          scan: { isbn13: found.isbn13, sourceLabel: '', bare: true },
          pendingRefs: [],
        };
      }

      const matchedAuthors = book.authors.flatMap((a) => (a.id ? [a.id] : []));
      const date = book.publishedDate?.slice(0, 10) ?? '';

      return {
        ...next,
        title: book.title || next.title,
        titleMl: book.titleMl || next.titleMl,
        description: book.description || next.description,
        language: book.language || next.language,
        pages: book.pages != null ? String(book.pages) : next.pages,
        // Some sources know only the year, which is not a date to save.
        publishedDate: /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : next.publishedDate,
        bindingType: book.bindingType ?? next.bindingType,
        authorIds: matchedAuthors.length ? matchedAuthors : next.authorIds,
        // One category only — the lookup already picked the themed one.
        categoryIds: book.category?.id ? [book.category.id] : next.categoryIds,
        publisherId: book.publisher?.id ?? next.publisherId,
        coverImageUrl: book.coverImageUrl ?? next.coverImageUrl,
        scan: { isbn13: found.isbn13, sourceLabel: sourceLine(found), bare: false },
        // Matched names are ticked above; the rest wait for staff to accept.
        pendingRefs: [
          ...book.authors
            .filter((a) => !a.id)
            .map((a) => ({ kind: 'author' as const, name: a.name })),
          ...(book.publisher && !book.publisher.id
            ? [{ kind: 'publisher' as const, name: book.publisher.name }]
            : []),
          ...(book.category && !book.category.id
            ? [{ kind: 'category' as const, name: book.category.name }]
            : []),
        ],
      };
    });

    setShowErrors(false);
    setSaveError(null);
    setPendingError(null);
    // The price is the next thing to type, and the one thing no lookup knows.
    window.setTimeout(() => mrpRef.current?.focus(), 0);
  }, []);

  const runLookup = useCallback(
    async (isbn: string) => {
      const seq = ++lookupSeq.current;
      setBusy(true);
      setLoadingEdit(false);
      setLookup(null);
      setLookupError(null);
      setScanHint(null);
      setNotice(null);
      try {
        const found = await catalogApi.lookupIsbn(isbn);
        if (seq !== lookupSeq.current) return;
        setLookup(found);
        setScanText('');
        // Two answers leave the form alone and put the choice on the card: a
        // book that is already catalogued, and a lookup that failed because a
        // source was down rather than because the book is unknown.
        if (found.existing) return;
        if (!found.book && found.unavailable.length) return;
        applyLookup(found);
      } catch (err) {
        if (seq !== lookupSeq.current) return;
        setLookupError(errorMessage(err, 'Could not look that ISBN up.'));
      } finally {
        if (seq === lookupSeq.current) setBusy(false);
      }
    },
    [applyLookup],
  );

  /** Open a catalogued book in the form, to correct it. */
  const loadForEdit = useCallback(async (id: string) => {
    const seq = ++lookupSeq.current;
    setBusy(false);
    setLookup(null);
    setLookupError(null);
    setScanHint(null);
    setScanText('');
    setNotice(null);
    setSaveError(null);
    setPendingError(null);
    setShowErrors(false);
    setLoadingEdit(true);
    try {
      const [book, saleIds] = await Promise.all([
        catalogApi.getBook(id),
        catalogApi.flashSaleBookIds(),
      ]);
      if (seq !== lookupSeq.current) return;

      // So the chips can name what the book already carries.
      setAuthors((prev) =>
        withOptions(
          prev,
          (book.authors ?? []).map(({ author }) => ({ id: author.id, name: author.name })),
        ),
      );
      setCategories((prev) =>
        withOptions(
          prev,
          (book.categories ?? []).map(({ category }) => ({ id: category.id, name: category.name })),
        ),
      );
      if (book.publisherId && book.publisher) {
        const publisher = { id: book.publisherId, name: book.publisher };
        setPublishers((prev) => withOptions(prev, [publisher]));
      }

      const loaded = formFromBook(book);
      editingRef.current = true;
      setCopyOf(null);
      setForm(loaded);
      setEditing({
        id: book.id,
        sku: book.sku,
        stockQuantity: book.stockQuantity ?? 0,
        isBook: (book.productType ?? 'BOOK') === 'BOOK',
        inFlashSale: saleIds.includes(book.id),
        baseline: loaded,
      });
    } catch (err) {
      if (seq !== lookupSeq.current) return;
      setLookupError(errorMessage(err, 'Could not load this book.'));
    } finally {
      if (seq === lookupSeq.current) setLoadingEdit(false);
    }
  }, []);

  /**
   * Start a new book that is a copy of a catalogued one: a used copy, or the
   * same book at the MRP the publisher has moved it to.
   *
   * Everything describing the edition is copied, the ISBN included — an ISBN
   * is unique per condition and MRP, so the copy shares it and a scan at
   * billing offers both. Not copied: the selling price and the stock, which
   * are this copy's own, and for a price copy the MRP too. It is saved as a
   * new book, never as an edit of the source.
   */
  const startCopy = useCallback(async (id: string, kind: CopySource['kind']) => {
    const seq = ++lookupSeq.current;
    setBusy(false);
    setLookup(null);
    setLookupError(null);
    setScanHint(null);
    setScanText('');
    setNotice(null);
    setSaveError(null);
    setPendingError(null);
    setShowErrors(false);
    setLoadingEdit(true);
    try {
      const book = await catalogApi.getBook(id);
      if (seq !== lookupSeq.current) return;

      setAuthors((prev) =>
        withOptions(
          prev,
          (book.authors ?? []).map(({ author }) => ({ id: author.id, name: author.name })),
        ),
      );
      setCategories((prev) =>
        withOptions(
          prev,
          (book.categories ?? []).map(({ category }) => ({ id: category.id, name: category.name })),
        ),
      );
      if (book.publisherId && book.publisher) {
        const publisher = { id: book.publisherId, name: book.publisher };
        setPublishers((prev) => withOptions(prev, [publisher]));
      }

      editingRef.current = false;
      setEditing(null);
      setForm({
        ...formFromBook(book),
        ...(kind === 'used' ? { isUsed: true } : { mrp: '' }),
        price: '',
        stock: '',
        isBestSeller: false,
        isDCopy: false,
        isOnlineVisible: book.isOnlineVisible !== false && !book.isDCopy,
      });
      setCopyOf({
        kind,
        id: book.id,
        title: book.title,
        mrp: toNumber(String(book.mrp)),
        groupId: book.groupId || mintGroupId(book.title ?? ''),
        linkSource: !book.groupId,
      });
      // The used price, or the new MRP, is the next thing to type.
      const next = kind === 'used' ? priceRef : mrpRef;
      window.setTimeout(() => next.current?.focus(), 0);
    } catch (err) {
      if (seq !== lookupSeq.current) return;
      setLookupError(errorMessage(err, 'Could not load this book.'));
    } finally {
      if (seq === lookupSeq.current) setLoadingEdit(false);
    }
  }, []);

  const editId = editRequest?.id;
  const editAsk = editRequest?.n;
  useEffect(() => {
    if (editId) void loadForEdit(editId);
  }, [editId, editAsk, loadForEdit]);

  // Anything that is not an ISBN is searched as a title (or SKU), through
  // the same lookup the billing screen uses.
  useEffect(() => {
    const term = scanText.trim();
    if (term.length < 2 || looksLikeIsbn(term)) {
      setTitleResults([]);
      setTitleSearching(false);
      setTitleError(null);
      return;
    }
    const controller = new AbortController();
    setTitleSearching(true);
    const timer = window.setTimeout(() => {
      posApi
        .lookup(term, controller.signal)
        .then((books) => {
          setTitleResults(books ?? []);
          setTitleError(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setTitleResults([]);
          setTitleError(errorMessage(err, 'Search failed.'));
        })
        .finally(() => {
          if (!controller.signal.aborted) setTitleSearching(false);
        });
    }, TITLE_SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [scanText]);

  /**
   * A book picked from the title results. One with an ISBN goes through the
   * ISBN lookup, so it lands on the same card a scan shows — Edit, Add at new
   * price and Add a used copy side by side. One without opens for editing.
   */
  function pickTitleResult(book: PosBook) {
    const isbn = book.isbn13?.trim();
    if (isbn && looksLikeIsbn(isbn)) {
      setScanText('');
      void runLookup(isbn);
    } else {
      void loadForEdit(book.id);
    }
  }

  function onScanKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = scanText.trim();
    if (!term || busy || loadingEdit) return;
    if (!looksLikeIsbn(term)) {
      // A single title match is unambiguous; open it. Several, pick below.
      if (titleResults.length === 1) {
        pickTitleResult(titleResults[0]);
        return;
      }
      if (titleResults.length > 1 || titleSearching) return;
      // Price add-on strips, shelf labels and loyalty cards all scan happily.
      setScanHint(
        'No book matched. Scan the ISBN barcode on the back cover, type the 13-digit number under it, or type part of the title.',
      );
      return;
    }
    void runLookup(term);
  }

  // ── names the scan found that are not in our lists ──

  async function acceptPendingRef(ref: PendingRef) {
    const key = `${ref.kind}:${ref.name}`;
    setCreatingRef(key);
    setPendingError(null);
    try {
      if (ref.kind === 'author') {
        const created = await catalogApi.createAuthor(ref.name);
        setAuthors((prev) => withOptions(prev, [created]));
        setForm((prev) => ({
          ...prev,
          authorIds: prev.authorIds.includes(created.id)
            ? prev.authorIds
            : [...prev.authorIds, created.id],
        }));
      } else if (ref.kind === 'category') {
        const created = await catalogApi.createCategory(ref.name);
        setCategories((prev) => withOptions(prev, [created]));
        setForm((prev) => ({
          ...prev,
          categoryIds: prev.categoryIds.includes(created.id)
            ? prev.categoryIds
            : [...prev.categoryIds, created.id],
        }));
      } else {
        const created = await catalogApi.createPublisher(ref.name);
        setPublishers((prev) => withOptions(prev, [created]));
        set({ publisherId: created.id });
      }
      dismissPendingRef(ref);
    } catch (err) {
      setPendingError(errorMessage(err, `Could not add “${ref.name}”.`));
    } finally {
      setCreatingRef(null);
    }
  }

  function dismissPendingRef(ref: PendingRef) {
    setForm((prev) => ({
      ...prev,
      pendingRefs: prev.pendingRefs.filter(
        (p) => !(p.kind === ref.kind && p.name === ref.name),
      ),
    }));
  }

  // ── inline creates from the pickers ──

  const createAuthor = useCallback(async (name: string) => {
    const created = await catalogApi.createAuthor(name);
    setAuthors((prev) => withOptions(prev, [created]));
    return created;
  }, []);

  const createCategory = useCallback(async (name: string) => {
    const created = await catalogApi.createCategory(name);
    setCategories((prev) => withOptions(prev, [created]));
    return created;
  }, []);

  const createPublisher = useCallback(async (name: string) => {
    const created = await catalogApi.createPublisher(name);
    setPublishers((prev) => withOptions(prev, [created]));
    return created;
  }, []);

  /**
   * A D-copy rarely has a known author, and hunting for one at the counter is
   * friction nobody needs. Only fills an empty picker, and only on the box
   * being ticked, so a real author is never replaced. If the stand-in
   * cannot be found or made, the picker stays empty for staff to fill.
   */
  async function giveDefaultAuthor() {
    // A keychain or a toy being edited has no author and wants none.
    if (!isBook || form.authorIds.length) return;
    try {
      // Creating a name that exists hands back the one that is already there.
      const ref = await createAuthor(DEFAULT_COUNTER_AUTHOR);
      // Staff may have picked someone while the stand-in was being made.
      setForm((prev) => (prev.authorIds.length ? prev : { ...prev, authorIds: [ref.id] }));
    } catch {
      /* left for staff to pick */
    }
  }

  /**
   * Ticking D-copy takes a book that is online off the website in the same
   * gesture — the server rejects the two together. Unticking it does NOT put
   * the book back online: a mis-click should not quietly publish something,
   * and the till has no way to publish one at all.
   */
  function markDCopy(next: boolean) {
    set(next ? { isDCopy: true, isOnlineVisible: false } : { isDCopy: false });
    if (next) void giveDefaultAuthor();
  }

  // ── what stands between the form and a saved book ──

  const mrp = toNumber(form.mrp);
  const price = toNumber(form.price);
  const qty = /^\d+$/.test(form.stock.trim()) ? Number(form.stock.trim()) : 0;
  const title = form.title.trim();
  const off = mrp > 0 && price > 0 ? Math.max(0, Math.round(((mrp - price) / mrp) * 100)) : 0;
  const counterOnly = form.isDCopy || !form.isOnlineVisible;

  const isBook = editing?.isBook ?? true;
  const { can } = useAuth();
  // Prices on a book that already exists have their own switch on the admin
  // panel's Roles & Permissions screen. A new book always needs its prices.
  const priceBarred = !!editing && !can('products.edit_price');
  const priceLocked = (editing?.inFlashSale ?? false) || priceBarred;
  // The website needs a picture to list a book. Only a new book is held to it:
  // an edit can't change images from here, so refusing one over a cover the
  // book never had would block unrelated corrections.
  const coverMissing = !editing && !counterOnly && !form.coverImageUrl;
  const canMarkNoIsbn =!editing || !editing.baseline.isbn13;

  // Every book carries an ISBN-13: a real one, or the BLSM-A stand-in the
  // server hands out when "No ISBN" is ticked. It is what the till scans to
  // bill the book, so one saved without either can only be found by typing
  // its title. On an edit the rule holds wherever the book already has one —
  // it cannot be emptied — but a book catalogued without one before this rule
  // is not refused an unrelated correction over it.
  const isbnMissing =
    !form.noIsbn && !form.isbn13.trim() && (!editing || !!editing.baseline.isbn13);

  const problems = useMemo(() => {
    const list: string[] = [];
    if (!title) list.push('Enter the book title.');
    if (isbnMissing) list.push('Enter the ISBN-13, or tick “No ISBN”.');
    if (!priceLocked) {
      if (mrp <= 0) list.push('Enter an MRP greater than 0.');
      if (price <= 0) list.push('Enter a selling price greater than 0.');
      // A selling price above MRP shows as a negative discount on the website,
      // so it is blocked — but on an edit, only when this edit touches the
      // pair. A book that predates the rule should not have an unrelated
      // spelling fix refused over it.
      const pricingTouched =
        !editing ||
        price !== toNumber(editing.baseline.price) ||
        mrp !== toNumber(editing.baseline.mrp);
      if (pricingTouched && mrp > 0 && price > mrp) {
        list.push('Selling price cannot be more than the MRP.');
      }
      if (copyOf?.kind === 'price' && mrp === copyOf.mrp) {
        list.push(`The book already in the catalogue is at MRP ${money(mrp)}. Enter the new MRP printed on these copies.`);
      }
    }
    if (isBook && !form.authorIds.length) list.push('Choose at least one author.');
    if (coverMissing) list.push('Add a cover image, or untick “Sell online”.');
    return list;
  }, [
    title,
    isbnMissing,
    mrp,
    price,
    form.authorIds.length,
    editing,
    isBook,
    priceLocked,
    coverMissing,
    copyOf,
  ]);

  /** What an edit would send, or null when adding. */
  const changes = useMemo(() => (editing ? changesOf(form, editing) : null), [form, editing]);

  function resetForm() {
    lookupSeq.current += 1;
    editingRef.current = false;
    setEditing(null);
    setCopyOf(null);
    setLoadingEdit(false);
    setForm(EMPTY_FORM);
    setShowErrors(false);
    setSaveError(null);
    setPendingError(null);
    setLookup(null);
    setLookupError(null);
    setScanHint(null);
    setScanText('');
    setBusy(false);
    scanRef.current?.focus();
  }

  function askToSave() {
    if (saving || confirming) return;
    if (problems.length) {
      setShowErrors(true);
      setSaveError(problems.join(' '));
      return;
    }
    if (changes && !Object.keys(changes).length) {
      setSaveError('Nothing has been changed yet.');
      return;
    }
    setSaveError(null);
    setConfirming(true);
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);

    if (editing) {
      try {
        const updated = await catalogApi.updateBook(editing.id, changesOf(form, editing));
        const labelNote =
          form.noIsbn && updated.isbn13 && isStandInIsbn(updated.isbn13)
            ? ` No-ISBN label: ${updated.isbn13}. Print it as a Code 128 label and stick it on the book.`
            : '';
        resetForm();
        setNotice({
          heading: 'Changes saved',
          text: `“${updated.title}” was updated.${labelNote}`,
        });
      } catch (err) {
        setSaveError(
          err instanceof ApiError && err.isMaintenance
            ? 'These changes were not saved — the till was closed for maintenance. Save them again.'
            : errorMessage(err, 'The changes could not be saved.'),
        );
      } finally {
        setSaving(false);
        setConfirming(false);
      }
      return;
    }

    const payload: CreateBookPayload = {
      title,
      ...(form.titleMl.trim() ? { titleMl: form.titleMl.trim() } : {}),
      price,
      mrp,
      authorIds: form.authorIds,
      ...(form.categoryIds.length ? { categoryIds: form.categoryIds } : {}),
      ...(form.noIsbn
        ? { noIsbn: true }
        : {
            ...(form.isbn13.trim() ? { isbn13: form.isbn13.trim() } : {}),
            ...(form.isbn10.trim() ? { isbn10: form.isbn10.trim() } : {}),
          }),
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
      ...(form.language ? { language: form.language } : {}),
      ...(Number(form.pages) > 0 ? { pages: Number(form.pages) } : {}),
      ...(form.publisherId ? { publisherId: form.publisherId } : {}),
      ...(form.publishedDate ? { publishedDate: form.publishedDate } : {}),
      bindingType: form.bindingType,
      isUsed: form.isUsed,
      // A D-copy is never online, whatever the other box was left saying.
      isOnlineVisible: form.isDCopy ? false : form.isOnlineVisible,
      isDCopy: form.isDCopy,
      isBestSeller: form.isBestSeller,
      ...(form.coverImageUrl
        ? { coverImageUrl: form.coverImageUrl, imageUrls: [form.coverImageUrl] }
        : {}),
      // The server books these into the shop, where the till sells from.
      ...(qty > 0 ? { stockQuantity: qty } : {}),
      ...(copyOf?.kind === 'used' ? { groupId: copyOf.groupId } : {}),
      ...(copyOf?.kind === 'price' ? { newPriceCopy: true } : {}),
    };

    try {
      const book = await catalogApi.createBook(payload);
      const stockNote =
        qty > 0
          ? ` with ${qty} ${qty === 1 ? 'copy' : 'copies'} in stock — it can be billed now.`
          : ' with no stock. It cannot be billed until copies are added to it.';
      const labelNote =
        book.isbn13 && isStandInIsbn(book.isbn13)
          ? ` No-ISBN label: ${book.isbn13}. Print it as a Code 128 label and stick it on the book.`
          : '';
      // The copy already carries the group. A source that had none needs it
      // too, or neither lists the other. A failure leaves a good book behind,
      // so it is said in the notice rather than treated as a failed save.
      let linkNote = '';
      if (copyOf) {
        linkNote = ` Listed as another option of “${copyOf.title}”.`;
        if (copyOf.kind === 'used' && copyOf.linkSource) {
          try {
            await catalogApi.updateBook(copyOf.id, { groupId: copyOf.groupId });
          } catch (e) {
            linkNote = ` It could not be linked to “${copyOf.title}” (${errorMessage(
              e,
              'not saved',
            )}) — link the two from the admin panel's book page.`;
          }
        }
      }
      resetForm();
      setNotice({
        heading: 'Book added',
        text: `“${book.title}” was added${book.sku ? ` (SKU ${book.sku})` : ''}${stockNote}${labelNote}${linkNote}`,
      });
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.isMaintenance
          ? 'This book was not saved — the till was closed for maintenance. Add it again.'
          : errorMessage(err, 'The book could not be saved.'),
      );
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  }

  const err = (condition: boolean) => showErrors && condition;
  const languages = LANGUAGES.includes(form.language) || !form.language
    ? LANGUAGES
    : [...LANGUAGES, form.language];

  return (
    <div className="addbook">
      <section className="pane addbook-scan">
        <div>
          <label className="field-label" htmlFor="addbook-scan">
            Scan the barcode on the back cover
          </label>
          <input
            id="addbook-scan"
            ref={scanRef}
            className="input input-scan"
            value={scanText}
            onChange={(e) => {
              setScanText(e.target.value);
              setScanHint(null);
            }}
            onKeyDown={onScanKeyDown}
            placeholder="Scan, type the ISBN and press Enter, or type a title"
            autoComplete="off"
            spellCheck={false}
            maxLength={120}
          />
          <p className={scanHint ? 'error small' : 'muted small'} role={scanHint ? 'alert' : undefined}>
            {scanHint ??
              'The details fill themselves in from the ISBN. Hyphens are fine. Type a title to find a book to edit. No barcode? Fill the form by hand.'}
          </p>
          {scanText.trim().length >= 2 && !looksLikeIsbn(scanText.trim()) && (
            <div className="results">
              <div className="results-status" aria-live="polite">
                {titleSearching && <p className="muted small">Searching…</p>}
                {titleError && <p className="error small">{titleError}</p>}
                {!titleSearching && !titleError && titleResults.length === 0 && (
                  <p className="muted small">No books matched “{scanText.trim()}”.</p>
                )}
              </div>
              {titleResults.length > 0 && (
                <div className="results-list results-inline" aria-label="Books matching the title">
                  {titleResults.map((book) => (
                    <button
                      key={book.id}
                      type="button"
                      className="result"
                      onClick={() => pickTitleResult(book)}
                    >
                      <span className="result-main">
                        <span className="result-title">
                          <span className="title-text" title={book.title}>
                            {book.title}
                          </span>
                          {book.isDCopy ? <span className="tag tag-dcopy">D-COPY</span> : null}
                          {book.isUsed ? <span className="tag tag-used">USED</span> : null}
                          {repricedMrpOf(book, titleResults) !== null ? (
                            <span className="tag tag-mrp">
                              MRP {money(repricedMrpOf(book, titleResults))}
                            </span>
                          ) : null}
                        </span>
                        <span className="result-code">{book.sku ?? book.isbn13 ?? ''}</span>
                      </span>
                      <span className="result-right">
                        <span className="result-price">{money(book.price)}</span>
                        <span className="stock">{stockLabelOf(book)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {notice && (
          <div className="banner banner-ok" role="status">
            <strong>{notice.heading}</strong>
            <span>{notice.text}</span>
          </div>
        )}

        <div aria-live="polite">
          {(busy || loadingEdit) && (
            <div className="scan-card">
              <p className="scan-busy">
                <span className="spinner" aria-hidden="true" />
                {loadingEdit ? 'Loading the book…' : 'Looking the book up…'}
              </p>
            </div>
          )}

          {lookupError && (
            <div className="scan-card scan-card-bad">
              <strong>Could not fetch the book</strong>
              <p>{lookupError}</p>
            </div>
          )}

          {lookup && !busy && (
            <LookupCard
              result={lookup}
              applied={form.scan?.isbn13 === lookup.isbn13}
              onRetry={() => void runLookup(lookup.isbn13)}
              onFillByHand={() => applyLookup(lookup)}
              onEdit={(id) => void loadForEdit(id)}
              onAddUsedCopy={(id) => void startCopy(id, 'used')}
              onAddPriceCopy={(id) => void startCopy(id, 'price')}
            />
          )}
        </div>
      </section>

      <section className="pane addbook-form">
        <div className="addbook-head">
          <h2>{editing ? 'Edit book' : 'Add a book'}</h2>
          <button type="button" className="link-btn" onClick={resetForm}>
            {editing ? 'Cancel edit' : 'Clear'}
          </button>
        </div>

        {editing && (
          <p className="addbook-note">
            Editing a book that is already in the catalogue
            {editing.sku ? ` (SKU ${editing.sku})` : ''}. Only what you change is saved.
          </p>
        )}

        {copyOf?.kind === 'used' && (
          <p className="addbook-note">
            Used copy of “{copyOf.title}”. Its details are copied, ISBN included — at billing,
            scanning the barcode offers both and the USED one is picked. Type the used selling price.
          </p>
        )}

        {copyOf?.kind === 'price' && (
          <p className="addbook-note">
            “{copyOf.title}” at a new price. Its details are copied, ISBN included; the copies
            already here stay at MRP {money(copyOf.mrp)} with their own stock. Type the MRP printed
            on these copies — at billing, scanning the barcode offers both, tagged with their MRP.
          </p>
        )}

        {form.scan && (
          <p className="addbook-note">
            {form.scan.bare
              ? `ISBN ${form.scan.isbn13} entered from the scan — nothing was found for it, so the rest is up to you.`
              : `Filled from ISBN ${form.scan.isbn13} via ${form.scan.sourceLabel}. Check it against the book before saving, and type the MRP printed on the back.`}
          </p>
        )}

        <div className="form-grid">
        <div className="addbook-top form-wide">
          <div className="addbook-top-main">
          <label className="form-field">
            <span className="field-label">Title *</span>
            <input
              className={err(!title) ? 'input input-bad' : 'input'}
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Book title"
              autoComplete="off"
            />
            {hasMalayalam(form.title) && (
              <span className="addbook-warn small">
                This is the title customers see, so it should be in English letters. Put the
                Malayalam below and write the English here.
              </span>
            )}
          </label>

          {(hasMalayalam(form.title) || !!form.titleMl) && (
            <label className="form-field">
              <span className="field-label">Malayalam title</span>
              <input
                className="input"
                value={form.titleMl}
                onChange={(e) => set({ titleMl: e.target.value })}
                placeholder="ഓളവും തീരവും"
                autoComplete="off"
              />
            </label>
          )}
          </div>

          <div className="addbook-top-cover">
            {form.coverImageUrl && (
              <div className="addbook-cover-preview">
                <img src={form.coverImageUrl} alt="Cover" />
                <span className="muted small">
                  {editing
                    ? 'Current cover. Images are changed from the admin panel.'
                    : 'Cover for this book.'}
                </span>
                {!editing && (
                  <button type="button" className="link-btn" onClick={() => set({ coverImageUrl: '' })}>
                    Remove
                  </button>
                )}
              </div>
            )}

            {!editing && (
              <div className="form-field">
                <span className="field-label">
                  {form.coverImageUrl ? 'Replace cover image' : 'Cover image'}
                  {!counterOnly && !form.coverImageUrl ? ' *' : ''}
                </span>
                <div
                  className={err(coverMissing) ? 'input input-bad' : 'input'}
                  style={{
                    textAlign: 'center',
                    padding: '12px',
                    borderStyle: 'dashed',
                    ...(dragOver ? { background: 'rgba(0,0,0,0.06)' } : {}),
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!uploadingCover) setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const file = e.dataTransfer.files?.[0];
                    if (!file || uploadingCover) return;
                    if (!file.type.startsWith('image/')) {
                      setCoverError('That is not an image. Drop a jpg, png, webp or gif.');
                      return;
                    }
                    void uploadCover(file);
                  }}
                >
                  <span className="muted small">Drag and drop an image here, or choose one:</span>
                  <br />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    disabled={uploadingCover}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void uploadCover(file);
                    }}
                  />
                </div>
                {uploadingCover && <span className="muted small">Uploading…</span>}
                {coverError && (
                  <span className="error small" role="alert">
                    {coverError}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
          <label className="form-field">
            <span className="field-label">
              {!editing || editing.baseline.isbn13 ? 'ISBN-13 *' : 'ISBN-13'}
            </span>
            <input
              className={err(isbnMissing) ? 'input input-bad' : 'input'}
              value={form.noIsbn ? '' : form.isbn13}
              onChange={(e) => set({ isbn13: e.target.value })}
              placeholder={form.noIsbn ? 'BLSM-A-xxxxx assigned on save' : '978XXXXXXXXXX'}
              disabled={form.noIsbn}
              autoComplete="off"
              maxLength={20}
            />
          </label>

          <label className="form-field">
            <span className="field-label">ISBN-10</span>
            <input
              className="input"
              value={form.noIsbn ? '' : form.isbn10}
              onChange={(e) => set({ isbn10: e.target.value })}
              placeholder={form.noIsbn ? '—' : 'XXXXXXXXXX'}
              disabled={form.noIsbn}
              autoComplete="off"
              maxLength={13}
            />
          </label>

          {canMarkNoIsbn && (
          <label className="check-row form-wide">
            <input
              type="checkbox"
              checked={form.noIsbn}
              onChange={(e) => set({ noIsbn: e.target.checked })}
            />
            <span>
              No ISBN
              <span className="muted small">
                The book gets the next BLSM-A number on save. Print it as a Code 128 label so it
                scans.
              </span>
            </span>
          </label>
          )}
        </div>

        <h3 className="form-heading">Price and stock</h3>
        <div className="form-grid form-grid-3">
          <label className="form-field">
            <span className="field-label">MRP *</span>
            <input
              ref={mrpRef}
              className={err(mrp <= 0) ? 'input input-bad' : 'input'}
              value={form.mrp}
              onChange={(e) => set({ mrp: sanitizeMoney(e.target.value) })}
              inputMode="decimal"
              placeholder="0"
              autoComplete="off"
              disabled={priceLocked}
              // Short on purpose: a stray scan landing here must not be able to
              // turn a 13-digit barcode into a price.
              maxLength={8}
            />
          </label>

          <label className="form-field">
            <span className="field-label">Selling price *</span>
            <input
              ref={priceRef}
              className={err(price <= 0 || price > mrp) ? 'input input-bad' : 'input'}
              value={form.price}
              onChange={(e) => set({ price: sanitizeMoney(e.target.value) })}
              inputMode="decimal"
              placeholder="0"
              autoComplete="off"
              disabled={priceLocked}
              maxLength={8}
            />
          </label>

          <label className="form-field">
            <span className="field-label">Copies in stock</span>
            <input
              className="input"
              value={form.stock}
              onChange={(e) => set({ stock: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              inputMode="numeric"
              placeholder="0"
              autoComplete="off"
            />
          </label>

          {priceBarred && (
            <p className="addbook-warn form-wide">
              Your role cannot change the MRP or selling price. Ask the Super Admin to switch it
              on under Roles &amp; Permissions.
            </p>
          )}
          {priceLocked && !priceBarred && (
            <p className="addbook-warn form-wide">
              This book is in a running flash sale, so the price shown is the sale price, not the
              book’s own. The price cannot be changed from the till while the sale runs.
            </p>
          )}

          <p className="muted small form-wide">
            {form.scan && !form.scan.bare
              ? 'MRP and selling price are never part of ISBN data — take the MRP from the price printed on the book. '
              : ''}
            {price > mrp && mrp > 0
              ? 'Selling price is above the MRP.'
              : off > 0
                ? `${off}% off — the customer saves ${money(mrp - price)}.`
                : ''}{' '}
            {editing
              ? `Stock is the count the book should have now — it was ${editing.stockQuantity} when this was opened. The website sells the same copies.`
              : 'A book with no copies in stock cannot be billed.'}
          </p>
        </div>

        <h3 className="form-heading">Authors and classification</h3>
        {optionsError && (
          <p className="error error-block" role="alert">
            {optionsError}{' '}
            <button type="button" className="link-btn" onClick={() => void loadOptions()}>
              Try again
            </button>
          </p>
        )}
        <div className="form-grid">
          <div className="form-wide">
            <RefPicker
              id="addbook-authors"
              label={isBook ? 'Authors *' : 'Authors'}
              options={authors}
              value={form.authorIds}
              onChange={(authorIds) => set({ authorIds })}
              onPick={(ref) => setAuthors((prev) => withOptions(prev, [ref]))}
              search={catalogApi.searchAuthors}
              onCreate={createAuthor}
              error={
                err(isBook && !form.authorIds.length)
                  ? 'At least one author is needed — pick one or add it here.'
                  : undefined
              }
            />
          </div>

          {form.pendingRefs.length > 0 && (
            <div className="pending form-wide">
              <strong>Found on the book, not in our lists yet</strong>
              <span className="muted small">Add one to tick it here, or ignore it.</span>
              <div className="pending-chips">
                {form.pendingRefs.map((ref) => {
                  const key = `${ref.kind}:${ref.name}`;
                  return (
                    <span key={key} className="pending-chip">
                      <span className="pending-kind">{PENDING_LABELS[ref.kind]}</span>
                      {ref.name}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => void acceptPendingRef(ref)}
                        disabled={!!creatingRef}
                      >
                        {creatingRef === key ? 'Adding…' : 'Add'}
                      </button>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => dismissPendingRef(ref)}
                        disabled={!!creatingRef}
                      >
                        Ignore
                      </button>
                    </span>
                  );
                })}
              </div>
              {pendingError && (
                <p className="error small" role="alert">
                  {pendingError}
                </p>
              )}
            </div>
          )}

          <RefPicker
            id="addbook-categories"
            label="Categories"
            options={categories}
            value={form.categoryIds}
            onChange={(categoryIds) => set({ categoryIds })}
            onCreate={createCategory}
            loading={optionsLoading}
          />

          <RefPicker
            id="addbook-publisher"
            label="Publisher"
            options={publishers}
            value={form.publisherId ? [form.publisherId] : []}
            onChange={(ids) => set({ publisherId: ids[0] ?? '' })}
            onPick={(ref) => setPublishers((prev) => withOptions(prev, [ref]))}
            search={catalogApi.searchPublishers}
            onCreate={createPublisher}
            single
          />
        </div>

        <h3 className="form-heading">Where it sells</h3>
        <div className="form-checks">
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.isUsed}
              onChange={(e) => set({ isUsed: e.target.checked })}
            />
            <span>
              Used book
              <span className="muted small">
                Sold at a cheaper price. Shows a USED tag on the website.
              </span>
            </span>
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={!form.isDCopy && form.isOnlineVisible}
              disabled={form.isDCopy}
              onChange={(e) => set({ isOnlineVisible: e.target.checked })}
            />
            <span>
              Sell online
              <span className="muted small">
                Listed on the website and the app as well as sold at the counter. Untick for a
                counter-only item.
              </span>
            </span>
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={form.isDCopy}
              onChange={(e) => markDCopy(e.target.checked)}
            />
            <span>
              D-copy
              <span className="muted small">
                A damaged copy, sold cheap at the counter. Never listed online.
              </span>
            </span>
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={form.isBestSeller}
              onChange={(e) => set({ isBestSeller: e.target.checked })}
            />
            <span>
              Best seller
              <span className="muted small">
                {counterOnly
                  ? 'The tag and the carousel are on the website only, so this does nothing while the book is counter-only — it is kept, and takes effect if the book goes online.'
                  : 'Shows a BEST SELLER tag on the website, and puts the book in the Best Sellers carousel on the homepage.'}
              </span>
            </span>
          </label>
        </div>
        {counterOnly && (
          <p className="addbook-warn">
            {form.isDCopy ? 'D-copy — counter only. ' : 'Counter only. '}
            Billable at the till. It will not appear on the website or the app.
          </p>
        )}

        <h3 className="form-heading">Book details</h3>
        <div className="form-grid">
          <label className="form-field">
            <span className="field-label">Binding</span>
            <select
              className="input"
              value={form.bindingType}
              onChange={(e) => set({ bindingType: e.target.value as BindingType })}
            >
              {BINDINGS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>

          <label className="form-field">
            <span className="field-label">Language</span>
            <select
              className="input"
              value={form.language}
              onChange={(e) => set({ language: e.target.value })}
            >
              <option value="">English (default)</option>
              {languages.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>

          <label className="form-field">
            <span className="field-label">Pages</span>
            <input
              className="input"
              value={form.pages}
              onChange={(e) => set({ pages: e.target.value.replace(/\D/g, '').slice(0, 5) })}
              inputMode="numeric"
              placeholder="0"
              autoComplete="off"
            />
          </label>

          <label className="form-field">
            <span className="field-label">Published date</span>
            <input
              className="input"
              type="date"
              value={form.publishedDate}
              onChange={(e) => set({ publishedDate: e.target.value })}
            />
          </label>

          <label className="form-field form-wide">
            <span className="field-label">Description</span>
            <textarea
              className="input"
              rows={4}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="What the book is about"
            />
          </label>
        </div>

        {saveError && (
          <p className="error error-block" role="alert">
            {saveError}
          </p>
        )}

        <button
          type="button"
          className="btn btn-primary btn-complete"
          onClick={askToSave}
          disabled={saving || confirming}
        >
          {editing
            ? saving
              ? 'Saving…'
              : 'Save changes'
            : saving
              ? 'Adding…'
              : qty > 0
                ? `Add book · ${qty} in stock`
                : 'Add book'}
        </button>
      </section>

      {confirming && changes && (
        <ConfirmEdit
          title={title}
          changed={Object.keys(changes).map((key) =>
            key === 'stockQuantity'
              ? `stock (${editing?.stockQuantity ?? 0} → ${changes.stockQuantity})`
              : (FIELD_LABELS[key] ?? key),
          )}
          counterOnlyNote={
            changes.isOnlineVisible === false
              ? `${form.isDCopy ? 'D-copy — counter only.' : 'Counter only.'} It will come off the website and the app.`
              : changes.isOnlineVisible === true
                ? 'It will go on sale on the website and the app.'
                : null
          }
          saving={saving}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void save()}
        />
      )}

      {confirming && !changes && (
        <ConfirmAdd
          title={title}
          mrp={mrp}
          price={price}
          qty={qty}
          counterOnly={counterOnly}
          isDCopy={form.isDCopy}
          saving={saving}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void save()}
        />
      )}
    </div>
  );
}

/** What the last scan came back with, under the scan box. */
function LookupCard({
  result,
  applied,
  onRetry,
  onFillByHand,
  onEdit,
  onAddUsedCopy,
  onAddPriceCopy,
}: {
  result: IsbnLookupResult;
  /** The form is holding this scan's ISBN. */
  applied: boolean;
  onRetry: () => void;
  onFillByHand: () => void;
  onEdit: (bookId: string) => void;
  onAddUsedCopy: (bookId: string) => void;
  onAddPriceCopy: (bookId: string) => void;
}) {
  // Already catalogued: creating it again would be refused at the very end of
  // the form, so say so now. The second rows that are wanted share the ISBN:
  // a used copy, and the same book at the publisher's new MRP.
  if (result.existing) {
    const { existing, existingUsed: used } = result;
    const canAddUsed = existing.isActive && !existing.isUsed && !used;
    // Only against a server that knows price copies — it sends `copies`.
    const canAddPrice = existing.isActive && !existing.isUsed && !!result.copies;
    // Several copies at different MRPs: list each, since the MRP is what
    // tells them apart and the two lines below would hide all but one.
    const priced = (result.copies ?? []).filter((c) => !c.isUsed);
    const repriced = priced.length > 1;
    return (
      <div className="scan-card scan-card-warn">
        <strong>Already in the catalogue</strong>
        <div className="scan-book">
          {existing.coverImageUrl && (
            <img className="shelf-cover" src={existing.coverImageUrl} alt="" />
          )}
          <div>
            <span className="scan-book-title">
              {existing.title}
              {existing.isUsed ? <span className="tag tag-used">USED</span> : null}
            </span>
            <span className="muted small">
              ISBN {result.isbn13}
              {existing.isActive ? ` · ${existing.stockQuantity} in stock` : ' · deleted'}
            </span>
            {repriced &&
              priced.map((c) => (
                <span key={c.id} className="muted small">
                  New at MRP {money(c.mrp ?? 0)}
                  {c.isActive ? ` · ${c.stockQuantity} in stock` : ' · deleted'}
                </span>
              ))}
            {used && (
              <span className="muted small">
                Used copy too{used.isActive ? ` · ${used.stockQuantity} in stock` : ' · deleted'}
              </span>
            )}
          </div>
        </div>
        <p>
          {!existing.isActive
            ? 'It was deleted, and adding it a second time would be refused. An admin can restore it from the admin panel.'
            : existing.isUsed
              ? 'Bill it from New sale; copies are added from the admin panel or the Facility app.'
              : (existing.stockQuantity > 0 || repriced
                  ? 'It does not need adding — bill it from New sale.'
                  : 'It has no stock: copies are added to it from the admin panel or the Facility app.') +
                (canAddUsed
                  ? ' A second-hand copy at its own price is added as a used copy, with the same ISBN.'
                  : '') +
                (canAddPrice
                  ? ' To correct this book’s MRP, use “Edit this book”. Only when new copies arrive printed at a higher MRP while old ones are still on the shelf, use “Add at new price” — that creates a second book.'
                  : '')}
        </p>
        {existing.isActive && (
          // Edit comes first and filled: staff who came to correct the MRP kept
          // pressing "Add at new price" because it led the row, and created a
          // second book. Adding a copy is the rarer job, so it goes last —
          // each option on its own line under Edit, all of them always shown.
          <div className="scan-actions scan-actions-stack">
            <button type="button" className="btn btn-primary" onClick={() => onEdit(existing.id)}>
              {used ? 'Edit the new copy' : 'Edit this book'}
            </button>
            {used?.isActive && (
              <button type="button" className="btn btn-outline" onClick={() => onEdit(used.id)}>
                Edit the used copy
              </button>
            )}
            {canAddUsed && (
              <button type="button" className="btn btn-outline" onClick={() => onAddUsedCopy(existing.id)}>
                Add a used copy
              </button>
            )}
            {canAddPrice && (
              <button type="button" className="btn btn-outline" onClick={() => onAddPriceCopy(existing.id)}>
                Add at new price
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!result.book) {
    // A source that was unreachable is not a book that does not exist. Typing
    // the whole record in by hand is the wrong first move when the same scan
    // would fill itself once the source answers again.
    if (result.unavailable.length && !applied) {
      return (
        <div className="scan-card scan-card-warn">
          <strong>Could not look this up</strong>
          <p>
            Could not reach {downLine(result)}, so ISBN {result.isbn13} could not be looked up.
            Try again in a few minutes — or fill the form by hand if you would rather not wait.
          </p>
          <div className="scan-actions">
            <button type="button" className="btn btn-primary" onClick={onRetry}>
              Try again
            </button>
            <button type="button" className="btn" onClick={onFillByHand}>
              Fill the form by hand
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="scan-card scan-card-warn">
        <strong>{result.unavailable.length ? 'Not looked up' : 'Nothing found'}</strong>
        <p>
          {result.unavailable.length
            ? `ISBN ${result.isbn13} could not be looked up.`
            : `No record of ISBN ${result.isbn13} in Google Books or Open Library.`}{' '}
          The ISBN is in the form — fill in the rest by hand.
        </p>
      </div>
    );
  }

  const { book } = result;
  const meta = [
    book.publisher?.name,
    book.category?.name,
    book.language,
    book.publishedDate?.slice(0, 4),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="scan-card">
      <strong>Found this book</strong>
      <div className="scan-book">
        {book.coverImageUrl && <img className="shelf-cover" src={book.coverImageUrl} alt="" />}
        <div>
          <span className="scan-book-title">{book.title}</span>
          <span className="muted small">{book.authors.map((a) => a.name).join(', ')}</span>
          {meta && <span className="muted small">{meta}</span>}
          <span className="muted small">From {sourceLine(result)}</span>
        </div>
      </div>
      {result.unavailable.length > 0 && (
        <p className="addbook-warn">
          Could not reach {downLine(result)}, so some fields are blank that would normally fill
          themselves. Check the form before saving.
        </p>
      )}
      {!applied && (
        // The form has moved on to something else since; offer the way back.
        <div className="scan-actions">
          <button type="button" className="btn" onClick={onFillByHand}>
            Use these details
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The last look before the book exists. A catalogue record is not something
 * the till can take back, so what is about to be created is read out first —
 * and the counter-only choice is repeated in words, because the box that set
 * it is a long way up the form by the time someone reaches the button.
 */
function ConfirmAdd({
  title,
  mrp,
  price,
  qty,
  counterOnly,
  isDCopy,
  saving,
  onCancel,
  onConfirm,
}: {
  title: string;
  mrp: number;
  price: number;
  qty: number;
  counterOnly: boolean;
  isDCopy: boolean;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Focus lands on "Go back", as it does when confirming a sale: a scanner
  // ends every code with Enter, and Enter here must be the harmless direction.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  return (
    <div className="modal-backdrop" onClick={() => !saving && onCancel()}>
      <div
        className="modal modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-add-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-add-title" className="confirm-title">
          Add this book?
        </h2>
        <p className="muted small confirm-lede">Nothing is added until you confirm.</p>

        <div className="confirm-total">
          <span>{title}</span>
          <strong>{money(price)}</strong>
        </div>

        <dl className="confirm-meta">
          <div>
            <dt>MRP</dt>
            <dd>{money(mrp)}</dd>
          </div>
          <div>
            <dt>Stock</dt>
            <dd>
              {qty > 0
                ? `${qty} ${qty === 1 ? 'copy' : 'copies'}`
                : 'None — it cannot be billed until copies are added from the admin panel or the Facility app'}
            </dd>
          </div>
          <div>
            <dt>Sells</dt>
            <dd>
              {counterOnly
                ? `${isDCopy ? 'D-copy — counter only' : 'Counter only'}. Not on the website or the app.`
                : 'Online and at the counter'}
            </dd>
          </div>
        </dl>

        <div className="modal-actions">
          <button
            type="button"
            ref={cancelRef}
            className="btn"
            onClick={onCancel}
            disabled={saving}
          >
            Go back
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={saving}>
            {saving ? 'Adding…' : 'Yes, add it'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The last look before a catalogued book is changed: which book, and what. */
function ConfirmEdit({
  title,
  changed,
  counterOnlyNote,
  saving,
  onCancel,
  onConfirm,
}: {
  title: string;
  /** The fields being changed, in staff's words. */
  changed: string[];
  /** Said in words when this edit moves the book on or off the website. */
  counterOnlyNote: string | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // "Go back" takes the focus for the same reason it does on every dialog
  // here: a scanner's Enter must be the harmless direction.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  return (
    <div className="modal-backdrop" onClick={() => !saving && onCancel()}>
      <div
        className="modal modal-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="confirm-edit-title" className="confirm-title">
          Save these changes?
        </h2>
        <p className="muted small confirm-lede">{title}</p>

        <dl className="confirm-meta">
          <div>
            <dt>Changing</dt>
            <dd>{changed.join(', ')}</dd>
          </div>
        </dl>
        {counterOnlyNote && <p className="addbook-warn">{counterOnlyNote}</p>}

        <div className="modal-actions">
          <button
            type="button"
            ref={cancelRef}
            className="btn"
            onClick={onCancel}
            disabled={saving}
          >
            Go back
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'Yes, save'}
          </button>
        </div>
      </div>
    </div>
  );
}
